import { React, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Block from "./Block/Block";
import { useDispatch, useSelector } from "react-redux";
import "./Preview.css";
import { updateCurrentBlockState } from "../../../Redux/actions/projectActions";
import "../../../assets/styles/FontsStyle.css";
// import watermarkimg from "../../../assets/icons/Scrollx_Black.png"
import scrollxLogo from "../../../assets/icons/Brand_Logos/ScrollX_LOGO_White_new.png";
import {
  getProjectBackgroundStyle,
  resolveOpaqueMatteColor,
} from "../utils/projectBackground";
import PreviewPlaybackBar from "./PreviewPlaybackBar";
import usePreviewPlayback from "./usePreviewPlayback";
import {
  getPreviewBlankHeight,
  getPreviewLayoutPpf,
  persistStoredResolution,
  persistStoredSpeed,
  readStoredResolution,
  readStoredSpeed,
  resolutionsForAspectFrame,
} from "./previewPlaybackUtils";

// WYSIWYG: editor, fullscreen, and render all show the SAME canonical
// layout (Redux previewWidth = 800). On screen it is only ZOOMED to fit
// the aspect frame / display width (display-only, never reflows), so the
// preview is exactly the rendered video scaled down, on every device.
// Export re-lays it out at previewWidth × previewScale via
// updatePreviewScale (see ScrollRender.handleExport / isExporting here) —
// a pure scale of the same 800px layout, so wrapping cannot change.
// getPreviewBaseWidth only picks the on-screen DISPLAY width used when no
// aspect frame is selected.
const PreviewBlankScreen = ({ width, height, position, backgroundColor }) => (
  <div
    className={`previewBlankScreen previewBlankScreen--${position}`}
    data-html2canvas-ignore="true"
    aria-hidden="true"
    style={{
      width,
      height: `${height}px`,
      backgroundColor: backgroundColor || "transparent",
    }}
  />
);

const getPreviewBaseWidth = () => {
  const screenWidth = window.innerWidth;

  if (screenWidth < 1500) return 800;
  if (screenWidth < 1600) return 860;
  if (screenWidth < 1800) return 1130;
  if (screenWidth < 2000) return 1250;
  if (screenWidth < 2200) return 1400;
  if (screenWidth < 2410) return 1570;
  if (screenWidth < 2600) return 1675;

  return 1920;
};

// Keep inset in-flow (floats collapsed .blockWrapper and skipped PNG capture).
// Horizontal position comes from these classes — do not set `margin: Npx 0`
// on the block; that zeroes margin-left and breaks right inset.
const blockInsetClass = {
  default: "w-[100%] ",
  left: "w-[50%] mr-auto",
  right: "w-[50%] ml-auto",
};
const focusDiv = (idx, currentIdx) => {
  if (idx === currentIdx) {
    return "focusDiv";
  } else {
    return "";
  }
};

const getWindowSize = () => {
  return {
    winWidth: window.innerWidth,
    winHeight: window.innerHeight,
  };
};

const detectwindowSize = () => {
  const { winWidth, winHeight } = getWindowSize();
  console.log("detectwindowSize =>", winWidth);
  return winHeight;
};

const scrollTo = (parent, child, slct, size) => {
  let parent1 = document.getElementById(parent);
  let child1 = document.getElementById(child);

  if (child1 && parent1) {
    // const navbar = document.getElementById("navbar_1");
    // const navbarHeight = navbar ? navbar.offsetHeight : 0;

    const childRect = child1.getBoundingClientRect();
    const parentRect = parent1.getBoundingClientRect();
    const relativeTop = parent1.scrollTop + childRect.top - parentRect.top;

    // Center the focused block inside the preview frame using the frame's ACTUAL
    // visible height (parent1.clientHeight). This stays centered for every aspect
    // ratio (16:9, 4:3, 1.85:1, 2.39:1) and every viewport width — no hard-coded
    // offset that only worked at one frame size. If a block is taller than the
    // frame, align its top instead of pushing its middle to center.
    const offset =
      childRect.height >= parent1.clientHeight
        ? 0
        : (parent1.clientHeight - childRect.height) / 2;

    // Window width based offset
    // const winHeight = detectwindowSize();
    // const scrollOffset = (winHeight < 1200) ? (winHeight < 1000 ? (winHeight < 900 ? (winHeight < 800 ? 180 : 260) : 260) : 260) : (260);

    parent1.scrollTo({
      behavior: "smooth",
      top: relativeTop - offset,
    });
  }
};

const endLines = (extra_lines_below, idx) => {
  const count = Math.max(0, Number(extra_lines_below) || 0);
  return (
    <div
      id={idx + "endlines"}
      aria-hidden
      style={
        count > 0
          ? {
              height: `${count}em`,
              minHeight: `${count}em`,
              flexShrink: 0,
              overflow: "hidden",
            }
          : undefined
      }
    >
      {count > 0
        ? [...Array(count)].map((_, i) => <br key={i} />)
        : null}
    </div>
  );
};

const getSafeSize = () => {
  const screenWidth = window.innerWidth;

  if (screenWidth < 1500) {
    return { w: 800, h: 450 };
  }

  if (screenWidth < 1600) {
    return { w: 860, h: 484 };
  }

  if (screenWidth < 1800) {
    return { w: 1130, h: 635.625 };
  }
  if (screenWidth < 2000) {
    return { w: 1250, h: 703.125 };
  }
  if (screenWidth < 2200) {
    return { w: 1400, h: 787.5 };
  }
  if (screenWidth < 2410) {
    return { w: 1570, h: 883.125 };
  }
  if (screenWidth < 2600) {
    return { w: 1675, h: 942.1875 };
  }

  // 1920 and above
  return { w: 1250, h: 703.125 };
};

export default function Preview({
  projectData,
  isFullscreen,
  onFullscreenToggle,
  isRenderExport,
  setIsRenderExport,
  isTitleSafe,
  aspectRatio_FrameSize,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  // Same Project shell hosts /render; hide the live banner on that route.
  const isRenderPage = location.pathname.endsWith("/render");
  const previewRef = useRef(null);
  // Skip the mount run so the ratio effect only reacts to actual ratio changes.
  const didMountRatio = useRef(false);
  const wasFullscreenRef = useRef(false);
  const pendingFsViewRestoreRef = useRef(false);

  const isOptionSelected = useSelector(
    (state) => state.commonReducer.isOptionSelected,
  );
  // Track fontStyle so we can correct scroll after font size changes
  const fontStyle = useSelector((state) => state.project.fontStyle);
  const { previewWidth, previewScale } = useSelector(
    (state) => state?.application,
  );
  const playbackFps = Number(projectData?.settings?.playback_fps) || 23;
  const [previewResolution, setPreviewResolution] = useState(
    readStoredResolution,
  );
  const [previewSpeed, setPreviewSpeed] = useState(readStoredSpeed);

  const isExporting = previewScale !== 1;

  // Live measured size of the aspect-ratio frame the preview sits in.
  const [safeSize, setSafeSize] = useState(getSafeSize);

  // Editor display width: measured frame when an aspect ratio is selected,
  // otherwise the screen-tier base width.
  const isAspectFramed =
    aspectRatio_FrameSize && aspectRatio_FrameSize !== "None";
  const displayBaseWidth =
    isAspectFramed && safeSize.w ? safeSize.w : getPreviewBaseWidth();

  // Editor AND fullscreen show the canonical render layout (800) zoomed to
  // the display width, so the preview always matches the rendered video.
  // Zoom multiplies scrollHeight, so playback gets the same factor
  // (scrollScale) to keep the displayed running time canonical and
  // identical on every screen.
  const canonicalWidth = previewWidth || 800;
  const displayWidth = isFullscreen
    ? safeSize.w || canonicalWidth
    : displayBaseWidth;
  const viewScale = displayWidth / canonicalWidth;
  const layoutPpf = getPreviewLayoutPpf(
    previewResolution,
    previewSpeed,
    canonicalWidth,
  );

  const {
    isPlaying,
    isPlayingRef,
    progress,
    currentLabel,
    durationLabel,
    isPlaybarVisible,
    isAtLastBlock,
    syncProgressFromScroll,
    handlePlayPause,
    handleRestart,
    handleSeek,
    handleSeekStart,
    handleSeekEnd,
    onBarMouseEnter,
    onBarMouseLeave,
  } = usePreviewPlayback({
    previewRef,
    ppf: layoutPpf,
    playbackFps,
    isRenderPage,
    creditsLength: projectData?.credits?.length,
    scrollScale: viewScale,
    layoutVersion: previewResolution,
  });

  const handlePreviewResolutionChange = (value) => {
    setPreviewResolution(value);
    persistStoredResolution(value);
  };

  const handlePreviewSpeedChange = (value) => {
    const next = Number(value);
    setPreviewSpeed(next);
    persistStoredSpeed(next);
  };

  const resolutionOptions = useMemo(
    () => resolutionsForAspectFrame(aspectRatio_FrameSize),
    [aspectRatio_FrameSize],
  );

  useEffect(() => {
    if (resolutionOptions.includes(previewResolution)) return;
    const next = resolutionOptions[0];
    if (!next) return;
    setPreviewResolution(next);
    persistStoredResolution(next);
  }, [aspectRatio_FrameSize, previewResolution, resolutionOptions]);

  useEffect(() => {
    if (isRenderExport) {
      // window.location.reload();
      setIsRenderExport((prev) => !prev);
    }
  }, [isRenderExport]);

  const [size, setSize] = useState({
    winWidth: window.innerWidth,
    winHeight: window.innerHeight,
  });
  const detectSize = () => {
    setSize({ winWidth: window.innerWidth, winHeight: window.innerHeight });
    detectwindowSize(size);
  };
  useEffect(() => {
    window.addEventListener("resize", detectSize);
    return () => {
      window.removeEventListener("resize", detectSize);
    };
  }, [size]);
  const currentBlockIdx = projectData?.currentBlock?.idx;

  // When font style changes (size, weight, etc.), correct the scroll position
  // so the focused block stays in place instead of drifting down.
  // getBoundingClientRect ensures no scroll happens if already in position.
  useEffect(() => {
    if (isPlayingRef.current) return;
    if (isOptionSelected === "font" && currentBlockIdx != null) {
      scrollTo("preview", "view_" + currentBlockIdx, "font", size);
    }
  }, [fontStyle]);

  // Same correction for layout option changes (margins, columns, block_inset, etc.)
  useEffect(() => {
    if (isPlayingRef.current) return;
    if (isOptionSelected === "layout" && currentBlockIdx != null) {
      scrollTo("preview", "view_" + currentBlockIdx, "font", size);
    }
  }, [projectData?.credits]);

  // Re-center the focused block when the aspect-ratio frame changes. The frame
  // resizes and CSS-transitions (~0.3s), so wait for it to settle, then center.
  useEffect(() => {
    if (!didMountRatio.current) {
      didMountRatio.current = true;
      return;
    }
    if (currentBlockIdx == null || isPlayingRef.current) return;
    const t = setTimeout(() => {
      scrollTo("preview", "view_" + currentBlockIdx);
    }, 350);
    return () => clearTimeout(t);
  }, [aspectRatio_FrameSize]);

  const onclick = (currentCreditBlockId, idx) => {
    dispatch(updateCurrentBlockState(currentCreditBlockId, idx));
    if (!isPlayingRef.current) {
      scrollTo("preview", "view_" + idx, isOptionSelected, size);
      scrollTo("content", "content" + idx, size);
    }
  };

  useEffect(() => {
    if (isPlayingRef.current) return;
    try {
      scrollTo("preview", "view_" + currentBlockIdx, isOptionSelected, size);
      scrollTo("content", "content" + currentBlockIdx, size);
    } catch (error) {
      console.log(error);
    }
  }, [currentBlockIdx, isOptionSelected]);

  useEffect(() => {
    if (wasFullscreenRef.current === isFullscreen) return;
    wasFullscreenRef.current = isFullscreen;
    // Keep restoring the focused block while zoom/frame settle after toggle.
    pendingFsViewRestoreRef.current = true;
    const done = setTimeout(() => {
      pendingFsViewRestoreRef.current = false;
    }, 400);
    return () => clearTimeout(done);
  }, [isFullscreen]);

  useEffect(() => {
    const getFrame = () =>
      document.querySelector("#preview-container .scrollFrame");

    const measure = () => {
      const frame = getFrame();

      if (frame) {
        const w = frame.clientWidth;
        const h = frame.clientHeight;

        if (w) {
          setSafeSize({
            w: Math.round(w),
            h: Math.round(h),
          });
          return;
        }
      }

      // Fallback
      setSafeSize(getSafeSize());
    };

    measure();

    const raf = requestAnimationFrame(measure);
    const frame = getFrame();
    let ro;

    if (frame && window.ResizeObserver) {
      ro = new ResizeObserver(measure);
      ro.observe(frame);
    }

    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [
    isFullscreen,
    aspectRatio_FrameSize,
    projectData?.resolution?.videoWidth,
    projectData?.resolution?.videoHeight,
  ]);

  // After fullscreen zoom/frame size settles, keep the same credit block centered
  // that was visible in the non-fullscreen preview (don't jump to a different slice).
  useEffect(() => {
    if (!pendingFsViewRestoreRef.current) return;

    const t = setTimeout(() => {
      if (!isPlayingRef.current && currentBlockIdx != null) {
        const el = previewRef.current;
        const child = document.getElementById("view_" + currentBlockIdx);
        if (el && child) {
          const childRect = child.getBoundingClientRect();
          const parentRect = el.getBoundingClientRect();
          const relativeTop = el.scrollTop + childRect.top - parentRect.top;
          const offset =
            childRect.height >= el.clientHeight
              ? 0
              : (el.clientHeight - childRect.height) / 2;
          el.scrollTo({
            behavior: "auto",
            top: Math.max(0, relativeTop - offset),
          });
        }
      }
      syncProgressFromScroll();
    }, 50);

    return () => clearTimeout(t);
  }, [isFullscreen, safeSize.w, safeSize.h, currentBlockIdx]);

  const blockArr = projectData?.credits?.map(({ options, items, _id }, idx) => {
    return {
      block: (
        <div
          key={idx}
          className={
            "" +
            blockInsetClass[options.block_inset] +
            " viewBlock" +
            focusDiv(idx, currentBlockIdx)
          }
          block-title={options.block_name}
          onClick={(e) => {
            onclick(_id, idx);
          }}
          style={{
            // Padding (not margin) so html2canvas includes it in the
            // .blockWrapper capture box, and so it scales with export transform.
            paddingTop: `${options.margin_vertical ?? 0}px`,
            paddingBottom: `${options.margin_vertical ?? 0}px`,
          }}
        >
          {projectData?.settings && (
            <Block
              options={options}
              items={items}
              idx={idx}
              settings={projectData?.settings}
            />
          )}
          {endLines(options.extra_lines_below, idx)}
        </div>
      ),
      idx: idx,
      side_by_side: options.block_inset,
    };
  });

  let sideBySideBlocks = [];

  // Export re-lays credits out at previewWidth × previewScale (a pure scale
  // of the same canonical layout); on screen everything is the canonical
  // width — the wrapper zoom handles display fitting.
  const blockWrapperWidth = isExporting
    ? `${previewWidth * previewScale}px`
    : `${canonicalWidth}px`;

  const contentWidth = isExporting
    ? `${previewWidth}px`
    : `${canonicalWidth}px`;

  const titleSafeSize = safeSize;

  const previewBgStyle = {
    ...getProjectBackgroundStyle(projectData?.settings),
    backgroundColor: resolveOpaqueMatteColor(projectData?.settings),
  };
  console.log("previewBgStyle => ", previewBgStyle?.backgroundColor);

  let gobalBGcolor = previewBgStyle?.backgroundColor;
  console.log("gobalBGcolor => ", gobalBGcolor);

  // Blank lead-in/out frames match the canonical layout width (zoom wrapper
  // scales them with the credits); export uses the scaled capture width.
  const blankWidthPx = isExporting
    ? previewWidth * previewScale
    : canonicalWidth;
  const blankHeightPx = getPreviewBlankHeight(
    blankWidthPx,
    previewResolution,
  );
  const blankScreenWidth = `${blankWidthPx}px`;

  useEffect(() => {
    syncProgressFromScroll();
  }, [blankHeightPx, syncProgressFromScroll]);

  return (
    <div
      className={
        "previewCnt h-full" +
        (isPlaying ? " is-playing" : "") +
        (isFullscreen ? " is-fullscreen" : "")
      }
      style={previewBgStyle}
    >
      <div
        id="preview"
        className={"  scrollable"}
        ref={previewRef}
        style={previewBgStyle}
      >
        {/* Zoom wrapper: scales the canonical render layout to the display
            width (editor frame or fullscreen frame) — the preview IS the
            render, scaled. Render capture (generateCreditsPng) resets this
            zoom to 1 by id while photographing blocks — the zoom is
            display-only and never reflows the credits. */}
        <div
          id="preview-zoom-wrapper"
          style={
            isExporting
              ? undefined
              : {
                  width: canonicalWidth,
                  margin: "0 auto",
                  zoom: viewScale,
                }
          }
        >
          <PreviewBlankScreen
            width={blankScreenWidth}
            height={blankHeightPx}
            position="start"
            backgroundColor={previewBgStyle?.backgroundColor}
          />
          {blockArr?.map((data, idx) => {
            if (data.side_by_side === "sidebyside") {
              sideBySideBlocks.push(idx, idx + 1);
              return (
                <div
                  id={"view_" + idx}
                  className={"blockWrapper pr-prants"}
                  style={{
                    // width: `${previewWidth * previewScale}px`
                    width: blockWrapperWidth,

                    // , backgroundColor: gobalBGcolor
                  }}
                >
                  <div className={"watermark_displaynone"}></div>
                  <div
                    className="flex flex-row gap-x-[0px]"
                    style={{
                      width: contentWidth,
                      transform: `scale(${previewScale})`,
                      transformOrigin: "top left",
                    }}
                  >
                    <div
                      className={
                        "w-[50%] " +
                        (idx === currentBlockIdx ? "focusDiv" : "")
                      }
                    >
                      {data.block}
                    </div>
                    <div
                      className={
                        "w-[50%] " +
                        (idx + 1 === currentBlockIdx ? "focusDiv" : "")
                      }
                    >
                      {blockArr[idx + 1]?.block}
                    </div>
                  </div>
                </div>
              );
            } else {
              if (blockArr[idx - 1]?.side_by_side !== "sidebyside") {
                return (
                  <div
                    id={"view_" + idx}
                  className={`blockWrapper pr-prants ${
  idx === currentBlockIdx
    ? gobalBGcolor === "#000000"
      ? "focusDiv"
      : `border-2 border-dashed ${
          gobalBGcolor === "#ffffff"
            ? "border-[#000000] border-[2.5px]"
            : "border-[#ffffff80]"
        } rounded-[10px]`
    : ""
}`}
                    style={{
                      width: blockWrapperWidth,
                    }}
                  >
                    <div className={"watermark_displaynone"}></div>
                    <div
                      style={{
                        width: contentWidth,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                      }}
                    >
                      {data.block}
                    </div>
                  </div>
                );
              }
            }
          })}
          <PreviewBlankScreen
            width={blankScreenWidth}
            height={blankHeightPx}
            position="end"
            backgroundColor={previewBgStyle?.backgroundColor}
          />
        </div>
      </div>
      {isTitleSafe && !isRenderExport && (
        <div
          className="cardTitleSafe"
          aria-hidden="true"
          data-html2canvas-ignore="true"
          style={{
            // Exact size of the measured frame, so the 80% inner box is the
            // same fraction of the frame in editor and fullscreen alike.
            width: titleSafeSize.w,
            height: titleSafeSize.h,
          }}
        >
          <div className="cardTitleSafeBox" />
        </div>
      )}

      {isFullscreen && <img src={scrollxLogo} alt="" className="fss2" />}

      {!isRenderExport && !isRenderPage && (
        <PreviewPlaybackBar
          isPlaying={isPlaying}
          onPlayPause={handlePlayPause}
          onRestart={handleRestart}
          progress={progress}
          currentLabel={currentLabel}
          durationLabel={durationLabel}
          onSeek={handleSeek}
          onSeekStart={handleSeekStart}
          onSeekEnd={handleSeekEnd}
          speed={previewSpeed}
          onSpeedChange={handlePreviewSpeedChange}
          resolution={previewResolution}
          resolutionOptions={resolutionOptions}
          onResolutionChange={handlePreviewResolutionChange}
          isFullscreen={isFullscreen}
          onFullscreenToggle={onFullscreenToggle}
          isVisible={isPlaybarVisible}
          isCompact={Boolean(
            isAtLastBlock ||
              (currentBlockIdx != null &&
                projectData?.credits?.length > 0 &&
                currentBlockIdx >=
                  Math.max(0, projectData.credits.length - 5)),
          )}
          onBarMouseEnter={onBarMouseEnter}
          onBarMouseLeave={onBarMouseLeave}
        />
      )}
    </div>
  );
}
